/// <reference path="../../../typings/browser.d.ts" />

namespace Application.Controllers {
    'use strict';

    export interface ILinkModalScope extends ng.IScope {
        title: string;
        network: Models.INetwork;
        device: Models.Hotspot | Models.Probe | Models.NetworkNode | Models.Gateway;
        dhcpMsg: string;
        // interface is wrapped in an object to get binding with the view working
        nicModel: { networkInterface: Models.INetworkInterface };
        modalClose: () => void;
        modalSave: () => void;
        formatIPAddress: (itf: Models.INetworkInterface) => string;
        deleteLink: (nic: Models.INetworkInterface) => void;
        addLinkMode: boolean;
        addLink: () => void;
        cancelAddLink: () => void;
        preSelectInterface: () => Models.INetworkInterface;
        getConnectedInterfaces: () => Models.INetworkInterface[];
        getAvailableInterfaces: () => Models.INetworkInterface[];
    }

    /**
     * LinkModalController class
     */
    export class LinkModalController {
        public static templateUrl: string = "decorators/modals/link-modal.html";
        public static ID: string = "#id-link-modal";
        public static $name: string = "linkModalController";
        public static $inject: string[] = ['$scope',
                                           'close',
                                           '$timeout',
                                           'title',
                                           'network',
                                           'device',
                                           'addEdgeEvent',
                                           'messageTranslationsService',
        ];

        private $scope: ILinkModalScope;
        private close: (parameters: ngModal.ICloseParameters) => void;
        private originalDevice: Models.Hotspot | Models.Probe | Models.NetworkNode | Models.Gateway;
        private addedLinks: Models.INetworkInterface[];
        private removedLinks: Models.INetworkInterface[];

        /**
         * Constructor
         */
        constructor($scope: ILinkModalScope,
                    close: (parameters: ngModal.ICloseParameters) => void,
                    $timeout: ng.ITimeoutService,
                    title: string,
                    network: Models.INetwork,
                    device: Models.Hotspot | Models.Probe | Models.NetworkNode | Models.Gateway,
                    addEdgeEvent: boolean,
                    private messageTranslationsService: Services.MessageTranslationsService ) {
            this.close = close;
            this.$scope = $scope;
            this.$scope.title = title;
            this.$scope.network = network;
            this.$scope.device = device;
            this.$scope.addLinkMode = false;
            this.originalDevice = angular.copy(device);
            this.$scope.dhcpMsg = 'auto (DHCP)';
            this.addedLinks = [];
            this.removedLinks = [];

            $timeout(() => {
                window.altair_admin_common_init();
                window.altair_forms_validation();
            }, 500);

            this.$scope.nicModel = { networkInterface: undefined };

            this.$scope.preSelectInterface = (): Models.INetworkInterface => {
                let availableInterfaces: Models.INetworkInterface[] = this.$scope.device.getAvailableInterfaces();
                if (availableInterfaces && availableInterfaces.length > 0)
                    return availableInterfaces[0];
            };

            this.$scope.modalClose = (): void => {
                device = angular.copy(this.originalDevice);
                this.$scope.device = angular.copy(this.originalDevice);
                this.close(undefined);
            };

            this.$scope.modalSave = (): void => {
                if (Common.Utils.exists(this.$scope.nicModel.networkInterface)) {
                    this.$scope.addLink();
                }

                let result: {device: Models.ITopologyElement, addedLinks: Models.ILink[], removedLinks: Models.ILink[]} = {
                    device: this.$scope.device,
                    addedLinks: [],
                    removedLinks: [],
                };

                for (let nic of this.addedLinks) {
                    result.addedLinks.push(new Models.Link(this.$scope.network as Models.IRef, this.$scope.device as Models.IRef, nic));
                }

                for (let nic of this.removedLinks) {
                    result.removedLinks.push(new Models.Link(this.$scope.network as Models.IRef, this.$scope.device as Models.IRef, nic));
                }

                this.close(result);
            };

            this.$scope.formatIPAddress = (nic: Models.INetworkInterface): string => {
                return nic.dhcp ? "DHCP" : nic.ipAddress;
            };

            this.$scope.getConnectedInterfaces = (): Models.INetworkInterface[] => {
                return this.$scope.device.networkInterfaces.filter((nic: Models.INetworkInterface) => {
                    return Common.Utils.exists(nic.networkConnectedTo) && nic.networkConnectedTo.id === this.$scope.network.id;
                });
            };

            this.$scope.getAvailableInterfaces = (): Models.INetworkInterface[] => {
                return this.$scope.device.networkInterfaces.filter((nic: Models.INetworkInterface) => {
                    return !Common.Utils.exists(nic.networkConnectedTo);
                });
            };

            this.$scope.deleteLink = (nic: Models.INetworkInterface): void => {
                let i: number = this.findNicIndex(this.addedLinks, nic.id);
                if (i !== undefined) this.addedLinks.splice(i, 1);
                let j: number = this.findNicIndex(this.removedLinks, nic.id);
                if (j === undefined) this.removedLinks.push(nic);
                nic.networkConnectedTo = null; // tslint:disable-line
                nic.connectedToInternet = false;
            };

            this.$scope.addLink = (): void => {
                if (!this.$scope.addLinkMode && this.$scope.getAvailableInterfaces().length > 0) {
                    this.$scope.nicModel.networkInterface = this.$scope.preSelectInterface();
                    this.$scope.addLinkMode = true;
                } else if (this.$scope.addLinkMode && this.$scope.nicModel.networkInterface !== undefined) {
                    let isNicValid: boolean = this.$scope.nicModel.networkInterface.dhcp ||
                        Common.IpUtils.checkIpAgainstSubnetAndMask(this.$scope.nicModel.networkInterface.ipAddress,
                                                                   this.$scope.network.ipAddress,
                                                                   this.$scope.network.subnetMask);
                    if (isNicValid || this.$scope.network.isInternet()) {
                        let i: number = this.findNicIndex(this.$scope.device.networkInterfaces, this.$scope.nicModel.networkInterface.id);
                        this.$scope.device.networkInterfaces[i].networkConnectedTo = this.$scope.network;
                        this.$scope.device.networkInterfaces[i].connected = true;
                        this.$scope.device.networkInterfaces[i].connectedToInternet = this.$scope.network.isInternet() ? true : false;
                        let j: number = this.findNicIndex(this.addedLinks, this.originalDevice.networkInterfaces[i].id);
                        if (j === undefined) this.addedLinks.push(this.$scope.device.networkInterfaces[i]);
                        if (!Common.Utils.exists(this.$scope.getAvailableInterfaces()) || this.$scope.getAvailableInterfaces().length < 1)
                            this.$scope.addLinkMode = false;
                        else
                            this.$scope.nicModel.networkInterface = this.$scope.preSelectInterface();
                    } else {
                        this.messageTranslationsService.alertOnError('Something was wrong: the IP does not match the selected network.',
                                                            'bottom-center'); 
                    }
                }
            };

            this.$scope.cancelAddLink = (): void => {
                this.$scope.addLinkMode = false;
                this.$scope.nicModel.networkInterface = undefined;
            };

            if (addEdgeEvent) {
                this.$scope.addLink();
            }
        }

        private findNicIndex(nics: Models.INetworkInterface[], id: number): number {
            for (let i: number = 0; i < nics.length; i++) {
                if (nics[i].id === id) {
                    return i;
                }
            }
            return undefined;
        }
    }

}
